import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    InputSignal,
    OnDestroy,
    signal,
    Signal,
    viewChild,
    WritableSignal,
} from '@angular/core';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
    IqEventBusTransformationService,
} from '@dat/iq-event-bus';
import { IqTooltipTooltipComponent } from '@dat/iq-tooltip';
import { get, isEqual, last } from 'lodash';
import { DateTime } from 'luxon';
import z from 'zod';
import {
    legendMap,
    LegendSettings,
    RateSeriesTypes,
    setCurrentGraphData,
    setUpCurrentGraph,
    setUpGraph,
    setXAxisData,
} from './graph-builder';

enum Statuses {
    Loading = 'LOADING',
    None = 'NONE',
    Error = 'ERROR',
    Loaded = 'LOADED',
}
const StatusesSchema = z.nativeEnum(Statuses);

const EventBusRateSchema = z.object({
    value: z.number(),
    date: z.string(),
    mileage: z.number(),
    fuelPerMile: z.number().optional().nullable(),
    fuelPerTrip: z.number().optional().nullable(),
});

const RateTimeRangeSchema = z.union([
    z.literal('historical.13MONTHS'),
    z.literal('forecast.13MONTHS'),
    z.literal('forecast.35DAYS'),
    z.literal('historical.current'),
]);
const RatePayloadSchema = z.object({
    id: z.string(),
    high: z.array(EventBusRateSchema),
    low: z.array(EventBusRateSchema),
    average: z.array(EventBusRateSchema),
    loaded: z.boolean(),
    errored: z.boolean(),
    timeEscalation: z.string().optional().nullable(),
    key: RateTimeRangeSchema,
});
const StatusPayloadSchema = z.object({
    id: z.string(),
    status: StatusesSchema,
});
const RateTypePayloadSchema = z.object({
    id: z.string(),
    rateType: z.union([
        z.literal('BrokerCarrierSpot'),
        z.literal('ShipperCarrierContract'),
    ]),
});
const TimeRangePayloadSchema = z.object({
    id: z.string(),
    timeRange: RateTimeRangeSchema,
});
const RateMapSchema = z.record(RateTimeRangeSchema, RatePayloadSchema);
const TripTypePayloadSchema = z.object({
    id: z.string(),
    perLoad: z.boolean(),
});
const CurrentRatePayloadSchema = z.object({
    id: z.string(),
    high: z.number(),
    low: z.number(),
    average: z.number(),
    mileage: z.number(),
    timeEscalation: z.string(),
    loaded: z.boolean(),
    errored: z.boolean(),
});
const CurrentFuelSchema = z.object({
    id: z.string(),
    tripFuel: z.number(),
    mileFuel: z.number(),
});

type CurrentRatePayload = z.infer<typeof CurrentRatePayloadSchema>;
type EventBusRate = z.infer<typeof EventBusRateSchema>;
type TripTypePayload = z.infer<typeof TripTypePayloadSchema>;
type RateTypePayload = z.infer<typeof RateTypePayloadSchema>;
type RatePayload = z.infer<typeof RatePayloadSchema>;
type CurrentFuel = z.infer<typeof CurrentFuelSchema>;
@Component({
    selector: 'iq-graph-rates-all',
    standalone: true,
    imports: [CommonModule, IqTooltipTooltipComponent],
    templateUrl: './iq-graph-rates-all.component.html',
    styleUrl: './iq-graph-rates-all.component.scss',
})
export class IqGraphRatesAllComponent implements OnDestroy, AfterViewInit {
    id: InputSignal<string> = input<string>('');

    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );
    private _transformation: IqEventBusTransformationService = inject(
        IqEventBusTransformationService,
    );
    private _dispatchActions: Record<string, IqEventBusBrokerHandler> = {};

    tooltipIcon: string = `<span class="text-caption mt-1 material-symbols-outlined">help</span>`;

    //#region Writable Signals
    rateTypePayload: WritableSignal<RateTypePayload> = signal({});
    tripTypePayload: WritableSignal<TripTypePayload> = signal({});

    /**
     * Determins which of the group of series ids are selected.
     *
     * This only applies to groups that aren't the current rate.
     */
    selectedRate: WritableSignal<RateSeriesTypes[]> = signal([]);
    //#endregion

    //#region Graph Setup
    graph: Signal<ElementRef> = viewChild<ElementRef>('hybridGraph');
    currentGraph: Signal<ElementRef> =
        viewChild<ElementRef>('hybridCurrentGraph');
    chartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
    };
    currentChartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
    };

    isBrokerCarrierSpot: Signal<boolean> = computed(
        () => {
            return this.rateTypePayload().rateType === 'BrokerCarrierSpot';
        },
        { equal: isEqual },
    );

    isShipperCarrierContract: Signal<boolean> = computed(
        () => {
            return this.rateTypePayload().rateType === 'ShipperCarrierContract';
        },
        { equal: isEqual },
    );

    legend: Signal<LegendSettings[]> = computed(
        () => {
            const isBrokerCarrierSpot = this.isBrokerCarrierSpot();
            const isShipperCarrierContract = this.isShipperCarrierContract();
            const selectedRate = this.selectedRate();
            const legendNodes = this.generateLegendNodes(
                isBrokerCarrierSpot,
                selectedRate,
                isShipperCarrierContract,
            );

            return [...legendNodes];
        },
        { equal: isEqual },
    );

    //#region ComputedSignals
    loaded: Signal<boolean> = computed(
        () => {
            return true;
        },
        { equal: isEqual },
    );

    currentData: Signal<any> = computed(
        () => {
            const currentRate =
                this.rateTypePayload().rateType === 'BrokerCarrierSpot'
                    ? this.brokerCarrierSpotCurrent()
                    : this.shipperCarrierContractCurrent();

            if (!currentRate) return {};
            const dataMap = new Map();
            const fuelPerMile = this.currentFuel().mileFuel;
            const calculatedData = {
                high: currentRate.high,
                low: currentRate.low,
                average: currentRate.average,
            };
            const timeEscalation =
                {
                    SEVENDAYS: 'Last 7\n Days',
                    FIFTEENDAYS: 'Last 15\n Days',
                    THIRTYDAYS: 'Last 30\n Days',
                    SIXTYDAYS: 'Last 60\n Days',
                    NINETYDAYS: 'Last 90\n Days',
                    THREEDAYS: 'Last 3\n Days',
                    ONEYEAR: 'Last Year',
                    ONEHUNDREDEIGHTYDAYS: 'Last 180\n Days',
                    ONEMONTH: 'Last Month',
                }[currentRate.timeEscalation ?? ''] ??
                currentRate.timeEscalation;
            dataMap.set(timeEscalation, {
                brokerCarrierSpotHistorical: calculatedData,
                shipperCarrierContractHistorical: calculatedData,
            });
            return dataMap;
        },
        { equal: isEqual },
    );

    //#region Helper Functions
    categoryData: Signal<Map<string, { orderIndex: number }>> = computed(
        () => {
            const historicalSpot = this.brokerCarrierSpotHistorical();
            const historicalContract = this.shipperCarrierContractHistorical();
            const forecastSpot = this.brokerCarrierSpotForecast();
            const forecastContract = this.shipperCarrierContractForecast();
            const perLoad = this.tripTypePayload().perLoad;
            const currentFuelPerMile = this.currentFuel().mileFuel;

            return this.generateCategories(
                historicalSpot,
                historicalContract,
                forecastSpot,
                forecastContract,
                perLoad,
                currentFuelPerMile,
            );
        },
        { equal: isEqual },
    );

    //#endregion
    statusInfo: WritableSignal<{ loaded: boolean; errored: boolean }> = signal({
        loaded: false,
        errored: false,
    });
    brokerCarrierSpotForecast: WritableSignal<RatePayload> = signal({});
    shipperCarrierContractForecast: WritableSignal<RatePayload> = signal({});
    brokerCarrierSpotHistorical: WritableSignal<RatePayload> = signal({});
    shipperCarrierContractHistorical: WritableSignal<RatePayload> = signal({});
    brokerCarrierSpotCurrent: WritableSignal<CurrentRatePayload> = signal({});
    shipperCarrierContractCurrent: WritableSignal<CurrentRatePayload> = signal(
        {},
    );
    currentFuel: WritableSignal<CurrentFuel> = signal({});
    constructor() {
        const handlerOpts = {
            parentId: this.id(),
        };

        this._dispatchActions = this._eventBus.registerMap({
            'type:graph-all-status-update': {
                callback: (payload: any) => {
                    if (this.id() !== payload.id) return;
                    const test = StatusPayloadSchema.safeParse(payload);
                    if (!test.success) {
                        console.warn(
                            'type:graph-time-status-update Invalid Payload',
                        );
                        return;
                    }

                    const currentInfo = this.statusInfo();
                    const statusUpdateMap: Record<Statuses, () => void> = {
                        [Statuses.Loading]: () => {
                            this.statusInfo.set({
                                ...currentInfo,
                                loaded: false,
                            });
                        },
                        [Statuses.None]: () => {
                            this.statusInfo.set({
                                ...currentInfo,
                                loaded: true,
                            });
                        },
                        [Statuses.Error]: () => {
                            this.statusInfo.set({
                                ...currentInfo,
                                errored: true,
                                loaded: true,
                            });
                        },
                        [Statuses.Loaded]: () => {
                            this.statusInfo.set({
                                ...currentInfo,
                                errored: false,
                                loaded: true,
                            });
                        },
                    };
                    if (payload.status === 'LOADING') {
                        statusUpdateMap[payload.status]();
                    } else {
                        // Events cause the graph to flicker, so we add a delay to make it less noticeable while all data is still processing
                        setTimeout(() => {
                            statusUpdateMap[payload.status]();
                        }, 3000);
                    }
                },
            },
            'type:graph-rates-all-view-update': {
                callback: (payload: any) => {
                    if (this.id() !== payload.id) return;
                    const data = payload;
                    this.rateTypePayload.set({
                        id: payload.id,
                        rateType: data.rateType,
                    });
                    this.tripTypePayload.set({
                        id: payload.id,
                        perLoad: data.perLoad,
                    });
                    this.brokerCarrierSpotHistorical.set({
                        id: this.id(),
                        high: data.bcsHistoricalHigh,
                        low: data.bcsHistoricalLow,
                        average: data.bcsHistoricalAverage,
                        loaded: data.loaded,
                        errored: data.errored,
                    });
                    this.shipperCarrierContractHistorical.set({
                        id: this.id(),
                        high: data.sccHistoricalHigh,
                        low: data.sccHistoricalLow,
                        average: data.sccHistoricalAverage,
                        loaded: data.loaded,
                        errored: data.errored,
                    });
                    this.brokerCarrierSpotForecast.set({
                        id: this.id(),
                        high: data.bcsForecastHigh,
                        low: data.bcsForecastLow,
                        average: data.bcsForecastAverage,
                        loaded: data.loaded,
                        errored: data.errored,
                    });

                    this.shipperCarrierContractForecast.set({
                        id: this.id(),
                        high: data.sccForecastHigh,
                        low: data.sccForecastLow,
                        average: data.sccForecastAverage,
                        loaded: data.loaded,
                        errored: data.errored,
                    });

                    this.brokerCarrierSpotCurrent.set({
                        id: this.id(),
                        high: data.currentHigh[0].value,
                        low: data.currentLow[0].value,
                        average: data.currentAverage[0].value,
                        mileage: data.mileage,
                        timeEscalation: data.currentEscalation,
                        loaded: data.loaded,
                        errored: data.errored,
                    });
                    this.shipperCarrierContractCurrent.set({
                        id: this.id(),
                        high: data.currentHigh[0].value,
                        low: data.currentLow[0].value,
                        average: data.currentAverage[0].value,
                        mileage: data.mileage,
                        timeEscalation: data.currentEscalation,
                        loaded: data.loaded,
                        errored: data.errored,
                    });
                },
                ...handlerOpts,
            },
        });

        effect(() => {
            setXAxisData(
                this.chartInfo,
                this.categoryData(),
                this.selectedRate(),
                this.rateTypePayload().rateType,
                this.tripTypePayload().perLoad,
                this._transformation,
            );
        });

        effect(() => {
            setCurrentGraphData(
                this.currentChartInfo,
                this.categoryData(),
                this.currentData(),
                this.rateTypePayload().rateType,
                this.tripTypePayload().perLoad,
                this._transformation,
            );
        });
    }

    // #region Lifecycle Hooks
    ngOnDestroy() {
        this._eventBus.unregisterMap(this._dispatchActions);
        this.chartInfo.root.dispose();
        this.currentChartInfo.root.dispose();
    }

    ngAfterViewInit(): void {
        this.chartInfo = setUpGraph(this.graph());
        this.currentChartInfo = setUpCurrentGraph(
            this.currentGraph(),
            this.chartInfo,
        );
    }
    //#endregion

    generateCategories(
        historicalSpot: RatePayload,
        historicalContract: RatePayload,
        forecastSpot: RatePayload,
        forecastContract: RatePayload,
        perLoad: boolean,
        currentFuelPerMile: number,
    ) {
        if (
            !historicalSpot?.average?.length ||
            !historicalContract?.average?.length
        ) {
            return new Map();
        }
        const categoryMap = new Map();
        let orderIndex = 0;

        historicalSpot.average.forEach((val, index) => {
            const date = DateTime.fromISO(val.date, { zone: 'utc' });
            const formattedDate = date.toFormat('MMM yy');
            categoryMap.set(formattedDate, {
                orderIndex,
                brokerCarrierSpotHistorical: {
                    high: historicalSpot.high[index].value,
                    low: historicalSpot.low[index].value,
                    average: val.value,
                    date: historicalSpot.average[index].date,
                },
                shipperCarrierContractHistorical: {
                    high: historicalContract.high[index].value,
                    low: historicalContract.low[index].value,
                    average: historicalContract.average[index].value,
                    date: historicalContract.average[index].date,
                },
            });
            orderIndex++;
        });

        if (!forecastSpot?.average?.length || !forecastSpot?.average?.length) {
            return categoryMap;
        }
        // Add month for forecast:
        const forecastDateMonth = DateTime.fromISO(
            last(historicalSpot.average).date,
            { zone: 'utc' },
        ).plus({ months: 1 });
        categoryMap.set(forecastDateMonth.toFormat('MMM yy'), { orderIndex });
        forecastSpot.average.forEach((val, index) => {
            const date = DateTime.fromISO(val.date, { zone: 'utc' });
            const formattedDate = date.minus({ year: 1 }).toFormat('MMM yy');
            categoryMap.set(formattedDate, {
                ...categoryMap.get(formattedDate),
                brokerCarrierSpotForecast: {
                    high: forecastSpot.high[index].value,
                    low: forecastSpot.low[index].value,
                    date: forecastSpot.average[index].date,
                    average: val.value,
                },
                shipperCarrierContractForecast: {
                    high: forecastContract.high[index].value,
                    low: forecastContract.low[index].value,
                    date: forecastSpot.average[index].date,
                    average: forecastContract.average[index].value,
                },
            });
        });
        return categoryMap;
    }

    /**
     * Creates an array of legend node objects to be used inside of the
     * graph for the legend.
     *
     * @param isBrokerCarrierSpot - checks if the current rate is BrokerCarrierSpot
     * @param selectedRate - the array of selected rates
     * @param isShipperCarrierContract - checks if the current rate is ShipperCarrierContract
     * @returns an array of legend nodes based on the current state of the set up.
     */
    private generateLegendNodes(
        isBrokerCarrierSpot: boolean,
        selectedRate: RateSeriesTypes[],
        isShipperCarrierContract: boolean,
    ) {
        /**
         * The order of the legend nodes based on the selected rate.
         */
        const order: RateSeriesTypes[] = isBrokerCarrierSpot
            ? [
                  'brokerCarrierSpotForecast',
                  'brokerCarrierSpotHistorical',
                  'shipperCarrierContractForecast',
                  'shipperCarrierContractHistorical',
              ]
            : [
                  'shipperCarrierContractForecast',
                  'shipperCarrierContractHistorical',
                  'brokerCarrierSpotForecast',
                  'brokerCarrierSpotHistorical',
              ];
        /**
         * Checks to see if the brokerCarrierSpot is checked.
         *
         * It only applies if this rate is BrokerCarrierSpot.
         */
        const brokerCarrierSpotHistoricalChecked = !isBrokerCarrierSpot
            ? selectedRate.includes('brokerCarrierSpotHistorical')
            : true;

        /**
         * Checks to see if the shipperCarrierContract is checked.
         *
         * It only applies if this rate is ShipperCarrierContract.
         */
        const shipperCarrierContractHistoricalChecked =
            !isShipperCarrierContract
                ? selectedRate.includes('shipperCarrierContractHistorical')
                : true;

        /**
         * Checks to see if the brokerCarrierSpotForecast is checked.
         * it only applies if this rate is BrokerCarrierSpot.
         */
        const brokerCarrierSpotForecastChecked = !isBrokerCarrierSpot
            ? selectedRate.includes('brokerCarrierSpotForecast')
            : true;

        /**
         * Checks to see if the shipperCarrierContractForecast is checked.
         * it only applies if this rate is ShipperCarrierContract.
         */
        const shipperCarrierContractForecastChecked = !isShipperCarrierContract
            ? selectedRate.includes('shipperCarrierContractForecast')
            : true;

        /**
         * The updated settings for the legend nodes.
         */
        const updatedSettings: Partial<
            Record<RateSeriesTypes, Partial<LegendSettings>>
        > = {
            /**
             * The settings for the Broker Carrier Spot based on the current
             * state of the set up.
             */
            brokerCarrierSpotHistorical: {
                /**
                 * Only toggles if the current rate is Broker Carrier Spot
                 */
                toggle: !isBrokerCarrierSpot,
                /**
                 * Only applies if the current rate is Broker Carrier Spot
                 * and the brokerCarrierSpot group is selected
                 */
                checked: brokerCarrierSpotHistoricalChecked,
                /**
                 * The color if checked.
                 */
                color: brokerCarrierSpotHistoricalChecked
                    ? legendMap.brokerCarrierSpotHistorical.color
                    : 'grey',
            },
            shipperCarrierContractHistorical: {
                toggle: !isShipperCarrierContract,
                checked: shipperCarrierContractHistoricalChecked,
                color: shipperCarrierContractHistoricalChecked
                    ? legendMap.shipperCarrierContractHistorical.color
                    : 'grey',
            },
            brokerCarrierSpotForecast: {
                toggle: !isBrokerCarrierSpot,
                checked: brokerCarrierSpotForecastChecked,
                color: brokerCarrierSpotForecastChecked
                    ? legendMap.brokerCarrierSpotForecast.color
                    : 'grey',
            },
            shipperCarrierContractForecast: {
                toggle: !isShipperCarrierContract,
                checked: shipperCarrierContractForecastChecked,
                color: shipperCarrierContractForecastChecked
                    ? legendMap.shipperCarrierContractForecast.color
                    : 'grey',
            },
        };

        /**
         * The legend nodes based on the order.
         *
         * This also iterates over the updated settings to apply the changes.
         */
        const legendNodes = order.map((type): LegendSettings => {
            return {
                ...legendMap[type],
                ...updatedSettings[type],
            };
        });

        return legendNodes;
    }

    //#endregion
    //#region Event Handlers
    toggleRate(item: LegendSettings) {
        if (!item.toggle) return;

        /**
         * Finds the rate group type associated with the clicked item.
         */
        const type: RateSeriesTypes = Object.keys(legendMap).find(
            (l) => legendMap[l].id === item.id,
        ) as RateSeriesTypes;

        /**
         * The current selected rate group.
         */
        const selectedRate: RateSeriesTypes[] = this.selectedRate();

        /**
         * If the item is checked, then it will remove the rate group from the selected rate.
         */
        const newRates: RateSeriesTypes[] = item.checked
            ? selectedRate.filter((r) => r !== type)
            : [...selectedRate, type];

        this.selectedRate.set(newRates);
    }
}
